import { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import SoMSubNav from '../components/SoMSubNav';
import { clientsAPI, somHistoryAPI, somPromptsAPI } from '../lib/api';
import {
  formatMeasurementState,
  formatMentions,
  formatMethodology,
  formatProductionPath,
  formatRunStatus,
  formatScore,
  formatSomHistoryDate,
  formatUsablePrompts,
} from '../lib/somHistoryHelpers';
import { somPromptApiError } from '../lib/somPromptHelpers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

export default function SoMHistoryPage() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [tab, setTab] = useState('production');
  const [production, setProduction] = useState([]);
  const [customRuns, setCustomRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRun, setSelectedRun] = useState(null);

  useEffect(() => {
    clientsAPI.list()
      .then((r) => {
        setClients(r.data || []);
        if (r.data?.length) setClientId(r.data[0].id);
      })
      .catch((err) => toast.error(somPromptApiError(err)));
  }, []);

  const loadHistory = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError('');
    try {
      const [prodRes, customRes] = await Promise.all([
        somHistoryAPI.production(clientId),
        somPromptsAPI.customRuns(clientId),
      ]);
      setProduction(Array.isArray(prodRes.data) ? prodRes.data : []);
      setCustomRuns(customRes.data?.runs || []);
    } catch (err) {
      const msg = somPromptApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="som-history-page">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">
              SoM History
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">
              Production checks and custom Run Now executions are kept separate. Custom runs do not change the SoM score.
            </p>
          </div>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="w-full sm:w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" data-testid="som-history-client-select">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <SoMSubNav />

        <div className="flex flex-wrap items-center gap-2" data-testid="som-history-tabs">
          <button
            type="button"
            data-testid="som-history-tab-production"
            onClick={() => setTab('production')}
            className={`px-3 py-1.5 text-sm rounded-md ${tab === 'production' ? 'bg-[var(--accent-light)] text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]'}`}
          >
            Production Checks
          </button>
          <button
            type="button"
            data-testid="som-history-tab-custom"
            onClick={() => setTab('custom')}
            className={`px-3 py-1.5 text-sm rounded-md ${tab === 'custom' ? 'bg-[var(--accent-light)] text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]'}`}
          >
            Custom Runs
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-[var(--error-border)] bg-[var(--error-bg)] px-4 py-3 text-sm text-[var(--error)]">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" />
          </div>
        ) : tab === 'production' ? (
          <ProductionChecks records={production} />
        ) : (
          <CustomRuns records={customRuns} onSelect={setSelectedRun} />
        )}
      </div>

      <Dialog open={!!selectedRun} onOpenChange={(open) => { if (!open) setSelectedRun(null); }}>
        <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]" data-testid="som-custom-run-detail">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo' }}>Custom run details</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-[var(--text-primary)]">
            <p>Status: {formatRunStatus(selectedRun?.status)}</p>
            <p className="text-[var(--text-secondary)]">{selectedRun?.response_preview || selectedRun?.error || 'No preview.'}</p>
            <p className="text-xs font-mono text-[var(--text-tertiary)]">
              {selectedRun?.provider} · {selectedRun?.latency_ms}ms · {selectedRun?.citations_created || 0} citations
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function ProductionChecks({ records }) {
  if (!records.length) {
    return (
      <div className="nx-metric-card rounded-lg text-center py-12" data-testid="som-production-history-empty">
        <p className="text-[var(--text-primary)] font-semibold">No production checks yet</p>
        <p className="text-sm text-[var(--text-secondary)] mt-1">Production SoM checks will appear here after a 4-prompt run.</p>
      </div>
    );
  }
  return (
    <div className="nx-metric-card rounded-lg overflow-x-auto" data-testid="som-production-history">
      <h2 className="font-display text-sm font-bold text-[var(--text-primary)] px-4 pt-4">Production Checks</h2>
      <table className="nx-table min-w-[640px]">
        <thead>
          <tr>
            <th>Date</th>
            <th>Score</th>
            <th>Result</th>
            <th>Mentions</th>
            <th>Prompts</th>
            <th>Method</th>
            <th>Path</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <tr key={r.id} data-testid={`som-production-row-${r.execution_path || 'unknown'}`}>
              <td>{formatSomHistoryDate(r.created_at)}</td>
              <td className="text-[var(--text-primary)] font-mono">{formatScore(r)}</td>
              <td>{formatMeasurementState(r)}</td>
              <td>{formatMentions(r)}</td>
              <td>{formatUsablePrompts(r)}</td>
              <td>{formatMethodology(r)}</td>
              <td>{formatProductionPath(r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CustomRuns({ records, onSelect }) {
  if (!records.length) {
    return (
      <div className="nx-metric-card rounded-lg text-center py-12" data-testid="som-custom-history-empty">
        <p className="text-[var(--text-primary)] font-semibold">No custom runs yet</p>
        <p className="text-sm text-[var(--text-secondary)] mt-1">Run Now results for custom prompts appear here. They do not affect the production score.</p>
      </div>
    );
  }
  return (
    <div className="nx-metric-card rounded-lg overflow-x-auto" data-testid="som-custom-history">
      <h2 className="font-display text-sm font-bold text-[var(--text-primary)] px-4 pt-4">Custom Prompt Runs</h2>
      <table className="nx-table min-w-[640px]">
        <thead>
          <tr>
            <th>Prompt</th>
            <th>Date</th>
            <th>Status</th>
            <th>Provider</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <tr
              key={r.execution_id || r.ai_response_id}
              data-testid={`som-custom-run-row-${r.status}`}
              className="cursor-pointer"
              onClick={() => onSelect(r)}
            >
              <td className="text-[var(--text-primary)] max-w-md">{r.prompt_text}</td>
              <td>{formatSomHistoryDate(r.created_at)}</td>
              <td className={r.status === 'failed' ? 'text-[var(--error)]' : 'text-[var(--success)]'}>{formatRunStatus(r.status)}</td>
              <td className="capitalize">{r.provider || 'Unavailable'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
